import { FormDrawer, FormDrawerContent, Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TenantOptionResource } from "../../../shared/types/tenant";
import { tenantService } from "../shared/api/tenantService";
import { createUserTenantsService } from "../shared/api/userService";
import { UserTenantMultiSelect } from "../shared/select/UserTenantSelect";

interface Props {
	userId: Id;
	open: boolean;
	handleClose: () => void;
}

interface FormState {
	tenants: TenantOptionResource[];
}
export const UserTenantsAttachDrawer: FC<Props> = ({ open, handleClose, userId }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const service = useMemo(() => createUserTenantsService(userId), [userId]);

	const onSubmit = useCallback(
		async ({ tenants }: FormState) => {
			const res = await service.attachTenants(tenants.map((x) => x.id));

			if (isSuccess(res)) {
				showToast({
					title: t("Tenants"),
					body: t("Successfully attached to the user"),
					type: "success",
				});
				void queryClient.invalidateQueries({ queryKey: service.listKey });
				void queryClient.invalidateQueries({ queryKey: service.tenantOptionsByUserKey });
				void queryClient.resetQueries({ queryKey: tenantService.indexKey });
				handleClose();
				return true;
			}
			return false;
		},
		[handleClose, queryClient, service, showToast, t],
	);

	return (
		<FormDrawer
			open={open}
			onClose={handleClose}
			initialValues={{ tenants: [] }}
			title={t("Attach tenants")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<UserTenantMultiSelect name="tenants" label={t("Tenants")} required={true} userId={userId} />
				</FormDrawerContent>
			)}
			onSubmit={onSubmit}
		/>
	);
};
